/* eslint-disable react-refresh/only-export-components -- provider dan hook adalah satu API. */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { apiRequest, jsonBody } from "../lib/api";

export interface MemberUser {
  id: string;
  name: string;
  whatsappNumber: string;
  pointsBalance: number;
  mustChangePassword: boolean;
}

interface MemberContextValue {
  member?: MemberUser;
  loading: boolean;
  refresh: () => Promise<void>;
  login: (
    whatsappNumber: string,
    pin: string,
    turnstileToken: string,
  ) => Promise<void>;
  register: (name: string, whatsappNumber: string, pin: string, turnstileToken: string) => Promise<void>;
  logout: () => Promise<void>;
  changePin: (
    whatsappNumber: string,
    oldPin: string,
    newPin: string,
    turnstileToken: string,
  ) => Promise<string>;
}

const MemberContext = createContext<MemberContextValue | undefined>(undefined);

export function MemberProvider({ children }: { children: React.ReactNode }) {
  const [member, setMember] = useState<MemberUser>();
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      setMember(await apiRequest<MemberUser>("/api/member/me"));
    } catch {
      setMember(undefined);
    }
  }, []);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      void refresh().finally(() => setLoading(false));
    }, 0);
    return () => window.clearTimeout(timer);
  }, [refresh]);

  const authenticate = useCallback(
    async (
      mode: "login" | "register",
      whatsappNumber: string,
      pin: string,
      name?: string,
      turnstileToken?: string,
    ) => {
      const result = await apiRequest<MemberUser>(`/api/member/${mode}`, {
        method: "POST",
        ...jsonBody({
          whatsappNumber,
          pin,
          ...(name ? { name } : {}),
          turnstileToken,
        }),
      });
      setMember(result);
    },
    [],
  );

  const value = useMemo<MemberContextValue>(
    () => ({
      member,
      loading,
      refresh,
      login: (phone, pin, turnstileToken) =>
        authenticate("login", phone, pin, undefined, turnstileToken),
      register: (name, phone, pin, turnstileToken) =>
        authenticate("register", phone, pin, name, turnstileToken),
      logout: async () => {
        await apiRequest("/api/member/logout", {
          method: "POST",
          ...jsonBody({}),
        });
        setMember(undefined);
      },
      changePin: async (
        whatsappNumber,
        oldPin,
        newPin,
        turnstileToken,
      ) => {
        const result = await apiRequest<{ message: string }>(
          "/api/member/change-pin",
          {
            method: "POST",
            ...jsonBody({
              whatsappNumber,
              oldPin,
              newPin,
              turnstileToken,
            }),
          },
        );
        setMember(undefined);
        return result.message;
      },
    }),
    [authenticate, loading, member, refresh],
  );

  return <MemberContext.Provider value={value}>{children}</MemberContext.Provider>;
}

export function useMember(): MemberContextValue {
  const context = useContext(MemberContext);
  if (!context) throw new Error("useMember harus berada di dalam MemberProvider");
  return context;
}
